import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IFormError, IFormProps } from '../utils'
import { IAutenticacao } from '../../models'

interface ILoginFormFields {
  loginCliente: string
  loginUsuario: string
  senhaUsuario: string
}

interface IRequestNewPasswordFormFields {
  email: string
}

interface IValidateCodeFormFields {
  code: string
}

interface ICreateNewPasswordFormFields {
  password: string
  confirmPassword: string
}

const loginFormFieldsInitialState: ILoginFormFields = {
  loginCliente: '',
  loginUsuario: '',
  senhaUsuario: ''
}

const requestNewPasswordFormFieldsInitialState: IRequestNewPasswordFormFields = {
  email: ''
}

const validateCodeFormFieldsInitialState: IValidateCodeFormFields = {
  code: ''
}

const createNewPasswordFormFieldsInitialState: ICreateNewPasswordFormFields = {
  password: '',
  confirmPassword: ''
}

interface IAuthState {
  isValid: boolean
  token?: string
  authentication?: IAutenticacao
  sessionTimeoutSeconds: number
  loginTimestamp: number | null
  loginForm: IFormProps<ILoginFormFields>
  currentPasswordRecoveryStep: number
  requestNewPasswordForm: IFormProps<IRequestNewPasswordFormFields>
  validateCodeForm: IFormProps<IValidateCodeFormFields>
  createNewPasswordForm: IFormProps<ICreateNewPasswordFormFields>
  sessionTimer: {
    startTime: number | null;  // timestamp do início
    timeRemaining: number;     // segundos restantes
    isActive: boolean;
    isExpired: boolean;
  },
    // ✅ NOVOS CAMPOS
  lastValidationTime: number | null;
  validationIntervalId: number | null;
}

const initialState: IAuthState = {
  isValid: false,
  sessionTimeoutSeconds: Number(process.env.SESSION_TIMEOUT_SECONDS),
  loginTimestamp: null,
  loginForm: {
    errors: [],
    fields: loginFormFieldsInitialState,
    touched: false,
    delivered: false
  },
  currentPasswordRecoveryStep: 0,
  requestNewPasswordForm: {
    errors: [],
    fields: requestNewPasswordFormFieldsInitialState,
    touched: false,
    delivered: false,
  },
  validateCodeForm: {
    errors: [],
    fields: validateCodeFormFieldsInitialState,
    touched: false,
    delivered: false,
  },
  createNewPasswordForm: {
    errors: [],
    fields: createNewPasswordFormFieldsInitialState,
    touched: false,
    delivered: false,
  },
  sessionTimer: {
    startTime: null,
    timeRemaining: 300, // 5 minutos em segundos
    isActive: false,
    isExpired: false
  },
    // ✅ NOVOS CAMPOS
  lastValidationTime: null,
  validationIntervalId: null
};

export const validateLoginForm = (fields: ILoginFormFields): IFormError<keyof ILoginFormFields>[] => {
  const errors: IFormError<keyof ILoginFormFields>[] = []

  if (!fields.loginCliente) {
    errors.push({
      key: 'loginCliente',
      message: 'Login cliente é obrigatório'
    })
  } else if (!fields.loginUsuario) {
    errors.push({
      key: 'loginUsuario',
      message: 'Login usuário é obrigatório'
    })
  }
  if (!fields.senhaUsuario) {
    errors.push({
      key: 'senhaUsuario',
      message: 'Senha é obrigatória'
    })
  }
  return errors
}

export const validateRequestNewPasswordForm = (fields: IRequestNewPasswordFormFields): IFormError<keyof IRequestNewPasswordFormFields>[] => {
  const errors: IFormError<keyof IRequestNewPasswordFormFields>[] = []
  if (!fields.email) {
    errors.push({
      key: 'email',
      message: 'E-mail é obrigatório'
    })
  } else if (!fields.email.includes('@')) {
    errors.push({
      key: 'email',
      message: 'E-mail inválido'
    })
  }
  return errors
}

export const validateValidateCodeForm = (fields: IValidateCodeFormFields): IFormError<keyof IValidateCodeFormFields>[] => {
  const errors: IFormError<keyof IValidateCodeFormFields>[] = []
  if (!fields.code) {
    errors.push({
      key: 'code',
      message: 'Código é obrigatório'
    })
  } else {
    if (fields.code.length !== 6) {
      errors.push({
        key: 'code',
        message: 'Código inválido'
      })
    }
  }
  return errors
}

export const validateCreateNewPasswordForm = (fields: ICreateNewPasswordFormFields): IFormError<keyof ICreateNewPasswordFormFields>[] => {
  const errors: IFormError<keyof ICreateNewPasswordFormFields>[] = [];
  if (fields.password === '' || fields.password === undefined) {
    errors.push({
      key: 'password',
      message: 'Senha é obrigatória'
    })
  } else {
    const regexSpecialCharacter = /[!@#$%&*()_+=|<>?{}[\]~-]/
    const regexLetter = /[a-z]/
    const regexCapitalLetter = /[A-Z]/
    const regexNumber = /[0-9]/

    if (
      fields.password.length < 8 ||
      !regexSpecialCharacter.test(fields.password) ||
      !regexLetter.test(fields.password) ||
      !regexCapitalLetter.test(fields.password) ||
      !regexNumber.test(fields.password)
    ) {
      errors.push({
        key: 'password',
        message: `<ul style="font-weight: bold">
        <li style="color:${fields.password.length >= 8 ? '#2e7d32' : '#d32f2f'}">
          A senha deve ter no mínimo 8 caracteres
        </li>
        <li style="color:${regexLetter.test(fields.password) ? '#2e7d32' : '#d32f2f'}">
          Incluir pelo menos uma letra minúscula
        </li>
        <li style="color:${regexCapitalLetter.test(fields.password) ? '#2e7d32' : '#d32f2f'};">
          Incluir pelo menos uma letra maiúscula
        </li>
        <li style="color:${regexNumber.test(fields.password) ? '#2e7d32' : '#d32f2f'}">
          Incluir pelo menos um número
        </li>
        <li style="color:${regexSpecialCharacter.test(fields.password) ? '#2e7d32' : '#d32f2f'}">
          Incluir pelo menos um caractere especial
        </li>
        </ul>`
      })
    }
    if (fields.confirmPassword !== fields.password) {
      errors.push({
        key: 'confirmPassword',
        message: 'As senhas não coincidem'
      })
    }
  }
  return errors
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTokenState(state, action: PayloadAction<string>) {
      state.token = action.payload

      if (action.payload === 'not_valid' || action.payload === '') {
        state.isValid = false
      } else {
        state.isValid = true
      }
    },
    resetAuthState(state) {
      state.token = initialState.token
    },
    //Login Form Actions
    setLoginFormLoginClienteState(state, action: PayloadAction<{
      loginCliente: string
    }>) {
      state.loginForm.fields.loginCliente = action.payload.loginCliente
    },
    setLoginFormLoginUsuarioState(state, action: PayloadAction<{
      loginUsuario: string
    }>) {
      state.loginForm.fields.loginUsuario = action.payload.loginUsuario
    },
    setLoginFormSenhaUsuarioState(state, action: PayloadAction<{
      senhaUsuario: string
    }>) {
      state.loginForm.fields.senhaUsuario = action.payload.senhaUsuario
    },
    setLoginFormTouchedState(state, action: PayloadAction<{
      touched: boolean
    }>) {
      state.loginForm.touched = action.payload.touched
    },
    setLoginFormDeliveredState(state, action: PayloadAction<{
      delivered: boolean
    }>) {
      state.loginForm.delivered = action.payload.delivered
    },
    setLoginFormErrorsState(state, action: PayloadAction<{
      errors: IFormError<keyof ILoginFormFields>[]
    }>) {
      state.loginForm.errors = action.payload.errors
    },
    resetLoginFormState(state) {
      state.loginForm = {
        errors: [],
        fields: loginFormFieldsInitialState,
        touched: false,
        delivered: false
      }
    },
    //Password Recovery Actions
    setPasswordRecoveryStepState(state, action: PayloadAction<{
      step: number
    }>) {
      state.currentPasswordRecoveryStep = action.payload.step
    },
    // Request New Password Form Actions
    setRequestNewPasswordFormEmailState(state, action: PayloadAction<{
      email: string
    }>) {
      state.requestNewPasswordForm.fields.email = action.payload.email
    },
    setRequestNewPasswordFormTouchedState(state, action: PayloadAction<{
      touched: boolean
    }>) {
      state.requestNewPasswordForm.touched = action.payload.touched
    },
    setRequestNewPasswordFormDeliveredState(state, action: PayloadAction<{
      delivered: boolean
    }>) {
      state.requestNewPasswordForm.delivered = action.payload.delivered
    },
    setRequestNewPasswordFormErrorsState(state, action: PayloadAction<{
      errors: IFormError<keyof IRequestNewPasswordFormFields>[]
    }>) {
      state.requestNewPasswordForm.errors = action.payload.errors
    },
    resetRequestNewPasswordFormState(state) {
      state.requestNewPasswordForm = {
        errors: [],
        fields: requestNewPasswordFormFieldsInitialState,
        touched: false,
        delivered: false
      }
    },
    // Validate Code Form Actions
    setValidateCodeFormCodeState(state, action: PayloadAction<{
      code: string
    }>) {
      state.validateCodeForm.fields.code = action.payload.code
    },
    setValidateCodeFormTouchedState(state, action: PayloadAction<{
      touched: boolean
    }>) {
      state.validateCodeForm.touched = action.payload.touched
    },
    setValidateCodeFormDeliveredState(state, action: PayloadAction<{
      delivered: boolean
    }>) {
      state.validateCodeForm.delivered = action.payload.delivered
    },
    setValidateCodeFormErrorsState(state, action: PayloadAction<{
      errors: IFormError<keyof IValidateCodeFormFields>[]
    }>) {
      state.validateCodeForm.errors = action.payload.errors
    },
    resetValidateCodeFormState(state) {
      state.validateCodeForm = {
        errors: [],
        fields: validateCodeFormFieldsInitialState,
        touched: false,
        delivered: false
      }
    },
    // Create New Password Form Actions
    setCreateNewPasswordFormPasswordState(state, action: PayloadAction<{
      password: string
    }>) {
      state.createNewPasswordForm.fields.password = action.payload.password
    },
    setCreateNewPasswordFormConfirmPasswordState(state, action: PayloadAction<{
      confirmPassword: string
    }>) {
      state.createNewPasswordForm.fields.confirmPassword = action.payload.confirmPassword
    },
    setCreateNewPasswordFormTouchedState(state, action: PayloadAction<{
      touched: boolean
    }>) {
      state.createNewPasswordForm.touched = action.payload.touched
    },
    setCreateNewPasswordFormDeliveredState(state, action: PayloadAction<{
      delivered: boolean
    }>) {
      state.createNewPasswordForm.delivered = action.payload.delivered
    },
    setCreateNewPasswordFormErrorsState(state, action: PayloadAction<{
      errors: IFormError<keyof ICreateNewPasswordFormFields>[]
    }>) {
      state.createNewPasswordForm.errors = action.payload.errors
    },
    resetCreateNewPasswordFormState(state) {
      state.createNewPasswordForm = {
        errors: [],
        fields: createNewPasswordFormFieldsInitialState,
        touched: false,
        delivered: false
      }
    },
    setAuthenticationState(state, action: PayloadAction<IAutenticacao>) {
      state.authentication = action.payload
    },
    resetAuthenticationState(state) {
      state.authentication = initialState.authentication
    },

    // Session Timer Actions
    setStartSessionTimer(state) {
      state.sessionTimer.startTime = Date.now();
      state.sessionTimer.timeRemaining = 300;
      state.sessionTimer.isActive = true;
      state.sessionTimer.isExpired = false;
    },
    
    setUpdateSessionTimer(state, action: PayloadAction<{ timeRemaining: number }>) {
      state.sessionTimer.timeRemaining = action.payload.timeRemaining;
    },
    
    setExpireSessionTimer(state) {
      state.sessionTimer.isActive = false;
      state.sessionTimer.isExpired = true;
      state.sessionTimer.startTime = null;
      state.sessionTimer.timeRemaining = 0;
    },
    
    resetSessionTimer(state) {
      state.sessionTimer = initialState.sessionTimer;
    },

    // Adicione estas novas actions no final do authSlice

// Session Validation Actions
setSessionValid(state, action: PayloadAction<{ isValid: boolean }>) {
  state.isValid = action.payload.isValid;
},

setLastValidationTime(state, action: PayloadAction<{ timestamp: number }>) {
  state.lastValidationTime = action.payload.timestamp;
},

setValidationIntervalId(state, action: PayloadAction<{ intervalId: number | null }>) {
  state.validationIntervalId = action.payload.intervalId;
},

setSessionConfig(state, action: PayloadAction<{
  timeout: number,
  timestamp?: number
}>) {
  state.sessionTimeoutSeconds = action.payload.timeout;
  if (action.payload.timestamp) {
    state.loginTimestamp = action.payload.timestamp;
  }
},
  }
});

export const {
  setTokenState,
  resetAuthState,
  //Login Form Actions
  setLoginFormLoginClienteState,
  setLoginFormSenhaUsuarioState,
  setLoginFormLoginUsuarioState,
  setLoginFormTouchedState,
  setLoginFormDeliveredState,
  setLoginFormErrorsState,
  resetLoginFormState,
  // Request New Password Form Actions
  setPasswordRecoveryStepState,
  setRequestNewPasswordFormEmailState,
  setRequestNewPasswordFormTouchedState,
  setRequestNewPasswordFormDeliveredState,
  setRequestNewPasswordFormErrorsState,
  resetRequestNewPasswordFormState,
  // Validate Code Form Actions
  setValidateCodeFormCodeState,
  setValidateCodeFormTouchedState,
  setValidateCodeFormDeliveredState,
  setValidateCodeFormErrorsState,
  resetValidateCodeFormState,
  // Create New Password Form Actions
  setCreateNewPasswordFormPasswordState,
  setCreateNewPasswordFormConfirmPasswordState,
  setCreateNewPasswordFormTouchedState,
  setCreateNewPasswordFormDeliveredState,
  setCreateNewPasswordFormErrorsState,
  resetCreateNewPasswordFormState,
  resetAuthenticationState,
  setAuthenticationState,

  // Session Timer Actions
  setStartSessionTimer,
  setUpdateSessionTimer,
  setExpireSessionTimer,
  resetSessionTimer,
  setSessionValid,
  setLastValidationTime,
  setValidationIntervalId,
  setSessionConfig
} = authSlice.actions;

export const authReducer = authSlice.reducer;